// Olympe
import { CloudObject } from 'olympe';

// Rxjs
import { tap, map } from 'rxjs';

// Json
import { JSONPath } from 'jsonpath-plus';

// Internal
import { warn, trimPath } from './helpers';
import { getModelPropertiesName, findObjectProperty } from './cloudobjects';
import { isChevronCellExpanded } from './cells';

/**
 * @typedef RGWrapper
 * @property {!*} rgObject - The wrapped object
 * @property {!Array} rgChildren - Potential children of the object
 * @property {function():string} rgGetKey - Get a unique key
 * @property {string} _rgType - The type of the wrapped object
 * @property {number} _rgLevel - The level in the hierarchy
 * @property {function():!Array<string>} __rgProperties - Get all the properties name
 * @property {function(string):!*} __rgGetProperty - Get a property by its path
 * @property {function(string):!*} __rgGetValue - Get a value for a path
 * @property {function(string):!*} __rgGetObject - Get the object for a path
 */
export const RGWrapper = {};

/**
 * Init the internal data state, make sure it is an array
 * @param {!BrickContext} $ 
 * @param {!Observable} _data 
 * @return {!Observable}
 */
export function initWrappers($, _data) {
    // Observe the data
    return _data

        // Wrap all objects
        .pipe(map(data => data.map(d => rgWrap(d))))

        // Set the transformed data into _Data
        .pipe(tap(wrappers => $.set('_rgWrappers', wrappers)));
}

/**
 * @param {!*} object 
 * @param {number=} level 
 * @return {!RGWrapper} 
 */
export function rgWrap(object, level) {
    // Already wrapped -> rewrap missing properties
    if(object.hasOwnProperty('rgObject')) {
        const missingProps = ['rgChildren', 'rgGetKey', '_rgType', '_rgLevel',
            '__rgProperties', '__rgGetProperty', '__rgGetValue',
            '__rgGetObject'].filter(prop => !object.hasOwnProperty(prop));
        if(missingProps.length > 0) {
            const wrapped = rgWrap(object.rgObject, level ?? 0);
            return missingProps.reduce((acc, cur) => {
                acc[cur] = wrapped[cur];
                return acc;
            }, object);
        }
        return object;
    }

    // Tuple case
    if(Array.isArray(object)) {
        return wrapTuple(object, level ?? 0);
    }

    // CloudObject case
    if(object instanceof CloudObject) {
        return wrapCloudObject(object, level ?? 0);
    }

    // Object case
    if(typeof object === 'object') {
        return wrapObject(object, level ?? 0);
    }

    // Wrong type
    warn('some data are not of the following type: Object, CloudObject, Array');
    return defaultWrap(level ?? 0);
}

/**
 * @param {!Object} object 
 * @param {number} level 
 * @return {!RGWrapper} 
 */
function wrapObject(object, level) {
    return {
        rgObject: object,
        rgChildren: [],
        rgGetKey: () => Object.values(object).join('.'),
        _rgType: 'object',
        _rgLevel: level,
        __rgProperties: () => Object.keys(object),
        __rgGetProperty: path => trimPath(path),
        __rgGetValue: path => JSONPath({ path, json: object, wrap: false }),
        __rgGetObject: () => object
    };
}

/**
 * @param {!CloudObject} object 
 * @param {number} level 
 * @return {!RGWrapper} 
 */
function wrapCloudObject(object, level) {
    return {
        rgObject: object,
        rgChildren: [],
        rgGetKey: () => object.getTag(),
        _rgType: 'cloudobject',
        _rgLevel: level,
        __rgProperties: () => getModelPropertiesName(object),
        __rgGetProperty: path => findObjectProperty(object, path),
        __rgGetValue: path => object.get(findObjectProperty(object, path)),
        __rgGetObject: () => object
    };
}

/**
 * @param {!Array} object 
 * @param {number} level 
 * @return {!RGWrapper} 
 */
function wrapTuple(object, level) {
    const wrapped = object.map(o => rgWrap(o, level + 1));
    return {
        rgObject: object,
        rgChildren: [],
        rgGetKey: () => `[${wrapped.map(w => w.rgGetKey()).join('.')}]`,
        _rgType: 'tuple',
        _rgLevel: level,
        __rgProperties: () => wrapped.map((w, i) => w.__rgProperties().map(p => `$[${i}].${p}`)).flat(),
        __rgGetProperty: path => {
            const [index, subPath] = splitTuplePath(path);
            return wrapped[index].__rgGetProperty(subPath);
        },
        __rgGetValue: path => {
            const [index, subPath] = splitTuplePath(path);
            return wrapped[index].__rgGetValue(subPath);
        },
        __rgGetObject: path => {
            const [index, subPath] = splitTuplePath(path);
            return wrapped[index].__rgGetObject(subPath);
        }
    };
}

/**
 * @param {number=} level 
 * @return {!RGWrapper} 
 */
export function defaultWrap(level) {
    return wrapObject({}, level ?? 0);
}

/**
 * @param {string} path 
 * @returns {![number, string]} 
 */
function splitTuplePath(path) {
    const endBracket = path.indexOf(']');
    const index = parseInt(path.substring(path.indexOf('[') + 1, endBracket));
    const subPath = trimPath(path.substring(endBracket + 1));
    return [index, subPath];
}

/**
 * @param {!BrickContext} $ 
 * @param {!Array} wrappers 
 * @param {number} level 
 * @return {!Array}
 */
function flatten($, wrappers, level) {
    return wrappers.flatMap(wrapper => {
        const isExpanded = isChevronCellExpanded($, wrapper);
        const children = isExpanded ? flatten($, wrapper.rgChildren, level + 1) : [];
        return [{ wrapper, level }, ...children]; 
    });
}

/**
 * @param {!BrickContext} $ 
 * @return {!Array}
 */
export function getFlattenWrappers($) {
    return flatten($, $.get('_rgWrappers'), 0);
}
